import { FC, Fragment, useEffect, useMemo, useState } from "react";

import EmptyData from "../../../EmptyData/EmptyData";
import NftCard from "../../../NftCard/NftCard";
import "./VaultGallery.scss";
import { useParams } from "react-router";
import CreateProposalWizard from "../../../CreateProposalWizard/CreateProposalWizard";
import { toggleModalOpenClass } from "../../../../utilities/helpers";
import { useAnchorWallet } from "@solana/wallet-adapter-react";
import { vaultStore } from "../../../../contexts/vaultStore";
import { IDetailNftData } from "../../../../common/interfaces/common.interface";
import { ClubAction, NftListing } from "../../../../common/enums/common.enums";
import { getUserRoleForClub } from "../../../../solana/methods/clubs";
import { checkIfUserHasPermissionForAction } from "../../../../solana/common/helpers/common.helpers";
import { getProposalConfigurations } from "../../../../common/constants/clubs-wizard.constants";
import { ProposalTypeNew } from "../../../../common/interfaces/proposals.interface";
import { IClubBasicInfo } from "../../../../common/interfaces/clubs.interface";
import { clubsStore } from "../../../../contexts/clubStore";

const VaultGallery: FC<{
  galleryTitle: string;
  clubBasicInfo: IClubBasicInfo;
  isSummaryStep?: boolean;
}> = ({ galleryTitle, isSummaryStep, clubBasicInfo }) => {
  const { vault, initialLoading, userRole, treasury } = clubsStore();
  const {
    allNfts,
    nftsOnSale,
    activeNfts,
    nftsOnBids,
    saleNftMarketplace,
    setActiveNfts,
  } = vaultStore();

  const [fetchedUserRole, setFetchedUserRole] = useState(userRole);
  const [isCreateProposalModalActive, toggleCreateProposalModal] =
    useState(false);
  const [nftToSell, setNftToSell] = useState<IDetailNftData>();
  const searchParams = useParams();
  const wallet = useAnchorWallet();

  useEffect(() => {
    if (!isSummaryStep) {
      void fetchRoleForUser();
    }
    return () => {
      setActiveNfts(NftListing.AllNfts);
    };
  }, []);

  const fetchRoleForUser = async () => {
    try {
      if (wallet && searchParams["clubName"]) {
        const userRole = await getUserRoleForClub(
          wallet!,
          searchParams["clubName"]!
        );
        setFetchedUserRole(userRole?.role);
      }
    } catch (error) {}
  };

  const getListOfNftsToRender = useMemo(() => {
    if (isSummaryStep && activeNfts === NftListing.AllNfts && vault) {
      return vault?.nfts;
    }
    switch (activeNfts) {
      case NftListing.AllNfts:
        return allNfts;
      case NftListing.Bids:
        return nftsOnBids;
      case NftListing.Sales:
        if (saleNftMarketplace.length === 0) return nftsOnSale;
        else {
          return nftsOnSale.filter((nft) =>
            saleNftMarketplace.find((mp) => mp.toString() === nft.status)
          );
        }
      default:
        return allNfts;
    }
  }, [
    activeNfts,
    nftsOnSale,
    nftsOnBids,
    allNfts,
    vault,
    saleNftMarketplace,
    isSummaryStep,
  ]);

  const mapNftCards = useMemo(
    () =>
      getListOfNftsToRender.map((nft) => {
        return (
          <NftCard
            sellNft={(nft) => setNftToSell(nft)}
            isSummaryStep={isSummaryStep}
            isSellable={
              activeNfts === NftListing.AllNfts &&
              !!fetchedUserRole &&
              checkIfUserHasPermissionForAction(
                fetchedUserRole,
                clubBasicInfo.roleConfig,
                ClubAction.CreateMeProposal
              ) &&
              checkIfUserHasPermissionForAction(
                fetchedUserRole,
                clubBasicInfo.roleConfig,
                ClubAction.CreateP2PProposal
              )
            }
            isOnBid={activeNfts === NftListing.Bids}
            isOnSale={activeNfts === NftListing.Sales}
            key={Math.random()}
            nft={nft}
          />
        );
      }),
    [
      activeNfts,
      fetchedUserRole,
      getListOfNftsToRender,
      isSummaryStep,
      clubBasicInfo,
      nftsOnSale,
      nftsOnBids,
      allNfts,
      vault,
      saleNftMarketplace,
    ]
  );

  const canAddNewProposal =
    !initialLoading &&
    userRole &&
    checkIfUserHasPermissionForAction(
      userRole,
      clubBasicInfo.roleConfig,
      ClubAction.CreateP2PProposal
    ) &&
    checkIfUserHasPermissionForAction(
      userRole,
      clubBasicInfo.roleConfig,
      ClubAction.CreateMeProposal
    ) &&
    checkIfUserHasPermissionForAction(
      userRole,
      clubBasicInfo.roleConfig,
      ClubAction.CreateSolseaProposal
    );

  const toggleProposalModal = () => toggleCreateProposalModal(true);

  const isBuyNftButtonDisabled = useMemo(() => {
    if (treasury && clubBasicInfo && userRole) {
      const configItems = getProposalConfigurations(
        treasury!,
        clubBasicInfo.type,
        userRole,
        clubBasicInfo.roleConfig
      );

      return !!configItems.find(
        (item) => item.name === ProposalTypeNew.TreasuryVault && item.disabled
      );
    }
  }, [treasury, clubBasicInfo, userRole]);

  return (
    <Fragment>
      {nftToSell && (
        <CreateProposalWizard
          closeModal={() => {
            setNftToSell(undefined);
            toggleModalOpenClass(false);
          }}
          nftToSell={nftToSell}
        />
      )}
      {isCreateProposalModalActive && (
        <CreateProposalWizard
          closeModal={() => {
            toggleModalOpenClass(false);
            toggleCreateProposalModal(false);
          }}
          proposalToBuy={true}
        />
      )}
      {vault?.nfts.length === 0 &&
      nftsOnSale.length === 0 &&
      nftsOnBids.length === 0 ? (
        <EmptyData
          description="The club doesn't have any NFTs yet."
          title="No NFTs found"
          buttonTitle={canAddNewProposal ? "Buy NFT" : undefined}
          action={canAddNewProposal ? toggleProposalModal : undefined}
          disabled={isBuyNftButtonDisabled}
        />
      ) : (
        <div className="vault-gallery">
          <p className="vault-gallery__gallery-title">{galleryTitle}</p>
          <div className="vault-gallery__nft-list">{mapNftCards}</div>
        </div>
      )}
    </Fragment>
  );
};

export default VaultGallery;
